import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, ArrowRight, Loader } from "lucide-react";
import { toast } from "sonner";
import api from "../../utils/api";

export default function PriorityListModal({ isOpen, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setLoading(true);
    try {
      await api.post("/priority-list", form);
      setSuccess(true);
      toast.success("You're on the list!", { description: "We'll be in touch with early access details." });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setForm({ name: "", email: "", company: "", role: "" });
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100]"
            style={{ background: "rgba(0,0,0,0.75)" }}
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
            data-testid="priority-list-modal"
          >
            <div
              className="relative w-full max-w-lg rounded-2xl p-8 overflow-hidden"
              style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
            >
              {/* Glow effect */}
              <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10 pointer-events-none"
                style={{ background: "radial-gradient(circle, var(--cyan) 0%, transparent 70%)" }} />

              {/* Close */}
              <button
                onClick={handleClose}
                className="absolute top-5 right-5 p-1.5 rounded-lg transition-colors hover:bg-white/5"
                style={{ color: "var(--text-muted)" }}
                data-testid="priority-modal-close"
              >
                <X size={18} />
              </button>

              <AnimatePresence mode="wait">
                {!success ? (
                  <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <div className="overline-label mb-4">Core AI Summit 2027</div>
                    <h2 className="text-3xl font-black tracking-tight text-white mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                      Join the Priority List
                    </h2>
                    <p className="text-sm mb-8" style={{ color: "var(--text-muted)" }}>
                      Be first to know when tickets open, speakers are announced, and early-bird pricing drops.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            required
                            placeholder="Jane Smith"
                            className="input-dark"
                            data-testid="priority-name-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                            Work Email *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            required
                            placeholder="jane@company.com"
                            className="input-dark"
                            data-testid="priority-email-input"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                          Company
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={form.company}
                          onChange={handleChange}
                          placeholder="Acme Corp"
                          className="input-dark"
                          data-testid="priority-company-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs mb-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                          I am a...
                        </label>
                        <select
                          name="role"
                          value={form.role}
                          onChange={handleChange}
                          className="input-dark"
                          data-testid="priority-role-select"
                        >
                          <option value="">Select your role</option>
                          <option value="business_executive">Business Executive / Decision Maker</option>
                          <option value="enterprise_leader">Enterprise AI Leader / CXO</option>
                          <option value="developer">Developer / Technical Practitioner</option>
                          <option value="smb_owner">SMB Owner / Entrepreneur</option>
                          <option value="startup_founder">Startup Founder</option>
                          <option value="investor">Investor / VC</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <button
                        type="submit"
                        disabled={loading || !form.name || !form.email}
                        className="btn-primary w-full justify-center mt-2"
                        data-testid="priority-submit-btn"
                        style={{ opacity: loading || (!form.name || !form.email) ? 0.6 : 1 }}
                      >
                        {loading ? (
                          <><Loader size={14} className="animate-spin" /> Submitting...</>
                        ) : (
                          <>Secure My Spot <ArrowRight size={14} /></>
                        )}
                      </button>
                    </form>

                    <p className="text-xs text-center mt-4" style={{ color: "var(--text-muted)", opacity: 0.6 }}>
                      No spam. Unsubscribe anytime. Your data is protected.
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                    data-testid="priority-success-state"
                  >
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                      style={{ background: "rgba(0,212,255,0.1)", border: "1px solid rgba(0,212,255,0.3)" }}>
                      <CheckCircle size={32} style={{ color: "var(--cyan)" }} />
                    </div>
                    <h3 className="text-2xl font-black text-white mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                      You're on the list.
                    </h3>
                    <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
                      Welcome to Core AI Summit 2027. We'll send you early access details, speaker announcements, and exclusive updates.
                    </p>
                    <button onClick={handleClose} className="btn-outline">Done</button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
